import React, { useState } from "react";
import Container from "react-bootstrap/Container";
import Navbar from "react-bootstrap/Navbar";
import "./Navbar.css";
import { Dropdown, DropdownButton, Button, ButtonGroup } from "react-bootstrap";
import { CircleUser, Menu, MoreVertical, Users, LayoutGrid } from "lucide-react"; // Import MoreVertical icon
import { FaShieldAlt, FaUser } from "react-icons/fa";
import { useAuth } from "../Contexts/AuthContext";
import { useOnlineUsers } from "../Contexts/OnlineUsersContext";
import { isAdminLike } from "../../services/utils/utiltyFunctions";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";

interface NavbarProps {
  toggleMenu: () => void;
  selectedUserName: string;
  onUserSelect: (userId: string | null, username: string) => void;
  // True when the operator is browsing someone else's claimed systems
  // (the "view another user's dashboard" picker). Multi-View is disabled
  // in that mode so operators can't monitor systems they don't own.
  isViewingOtherUser?: boolean;
}

const NavBar: React.FC<NavbarProps> = ({
  toggleMenu,
  selectedUserName,
  onUserSelect,
  isViewingOtherUser,
}) => {
  const auth = useAuth();
  const { onlineUsers } = useOnlineUsers();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  
  const handleMenuClick = () => {
    if (location.pathname === '/dashboard') {
      toggleMenu();
    } else {
      navigate('/dashboard');
      // Small delay to ensure navigation completes before opening menu
      setTimeout(() => {
        toggleMenu();
      }, 100);
    }
  };

  return (
    <>
      <Navbar
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: 'none',
          borderBottom: '1px solid rgba(33, 150, 243, 0.1)',
          height: "3.5rem",
          boxShadow: '0 2px 20px rgba(0, 0, 0, 0.1)'
        }}
      >
      <Container className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          {auth.isAuthenticated && (
            <Button
              variant="outline"
              onClick={handleMenuClick}
              style={{ 
                color: "#2196F3",
                borderColor: "#E3F2FD",
                backgroundColor: "rgba(33, 150, 243, 0.1)",
                borderRadius: "12px",
                padding: "8px 12px"
              }}
            >
              <Menu className="h-4 w-4" />
            </Button>
          )}
        </div>

        <Navbar.Brand style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, #2196F3, #1976D2)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: '700',
          fontSize: '24px',
          letterSpacing: '-0.5px'
        }}>EpiCareAlert</Navbar.Brand>

        {auth.isAuthenticated && location.pathname === '/dashboard' && (
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            {/* Multi-View — opens the 2x2 monitoring grid. Desktop-only;
                the 2x2 video grid is not usable on a phone screen anyway.
                Dashboard owns the modal state and listens for the custom
                event so we don't have to thread props through App.tsx. */}
            {window.innerWidth > 768 && (
              <Button
                variant="outline-primary"
                onClick={() => {
                  if (isViewingOtherUser) return;
                  window.dispatchEvent(new Event("open-multi-view"));
                }}
                disabled={isViewingOtherUser}
                title={
                  isViewingOtherUser
                    ? "Multi-View is only available on systems you've claimed yourself"
                    : "Watch up to 4 systems simultaneously"
                }
                style={{
                  background: isViewingOtherUser ? 'rgba(148, 163, 184, 0.1)' : 'rgba(124, 58, 237, 0.1)',
                  border: `1px solid ${isViewingOtherUser ? 'rgba(148, 163, 184, 0.25)' : 'rgba(124, 58, 237, 0.25)'}`,
                  color: isViewingOtherUser ? '#94a3b8' : '#6d28d9',
                  borderRadius: '12px',
                  padding: '8px 12px',
                  fontWeight: '600',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: isViewingOtherUser ? 'not-allowed' : 'pointer',
                }}
              >
                <LayoutGrid size={14} />
                Multi-View
              </Button>
            )}

            <Button
              variant="outline-primary"
              onClick={() => setShowDropdown(!showDropdown)}
              className="online-users-btn"
              style={{
                background: 'rgba(33, 150, 243, 0.1)',
                border: '1px solid rgba(33, 150, 243, 0.2)',
                color: '#1565C0',
                borderRadius: '12px',
                padding: '8px 12px',
                fontWeight: '600',
                fontSize: '12px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '120px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Users size={14} />
              {window.innerWidth <= 768 ? selectedUserName.split(' ')[0] : selectedUserName} ▼
            </Button>
            {showDropdown && createPortal(
              <div
                style={{
                  position: 'fixed',
                  top: '60px',
                  right: '10px',
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(33, 150, 243, 0.1)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  padding: '8px',
                  zIndex: 9999999,
                  width: '200px'
                }}
              >
                <div style={{ color: '#1565C0', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '8px 16px 4px 16px' }}>Online Users</div>
                {onlineUsers.length > 0 ? (
                  onlineUsers.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => {
                        onUserSelect(
                          user.id === auth.user?.sub ? null : user.id,
                          user.id === auth.user?.sub ? "Online Users" : user.name
                        );
                        setShowDropdown(false);
                      }}
                      style={{
                        borderRadius: '8px',
                        margin: '2px 0',
                        padding: '8px 16px',
                        color: '#2c3e50',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(33, 150, 243, 0.1)';
                        e.currentTarget.style.color = '#1565C0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#2c3e50';
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {user.id === auth.user?.sub ? "Me" : user.name}
                      </span>
                      {isAdminLike(user.role) ? (
                        <FaShieldAlt size={12} color="#2563eb" title={user.role === "superadmin" ? "Super admin" : "Admin"} style={{ flexShrink: 0 }} />
                      ) : (
                        <FaUser size={11} color="#94a3b8" title="User" style={{ flexShrink: 0 }} />
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '8px 16px', color: '#6c757d' }}>No users online</div>
                )}
              </div>,
              document.body
            )}
          </div>
        )}
      </Container>
      </Navbar>
      {showDropdown && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999998
          }}
          onClick={() => setShowDropdown(false)}
        />
      )}
    </>
  );
};

export default NavBar;
